/**
 * 日曆例外（假日／加班日／特殊工時）的預先編譯與比對。
 *
 * 例外可能帶週期性（每年、每月、每週、每日重複）。為了避免展開成無限清單，
 * 這裡把每個例外編譯成「以天數為單位的規則」，查詢時以 O(例外數) 反推是否命中；
 * 呼叫端（ResolvedCalendar）再對「每一天的解析結果」做快取，因此高頻查詢仍是 O(1)。
 *
 * 週期規則：Monthly／Yearly 保留「起始日的月中日期」。若某一次發生的日期在該月
 * 不存在（每月 31 號遇到小月、每年 2/29 遇到平年），該次發生會被**跳過**，
 * 不會夾到月底、也不會捲到下個月。
 */

import type { CalendarException, WorkingTime } from '../domain/calendar.js';
import {
  civilFromDays,
  daysFromCivil,
  isValidCivilDate,
  parseDate,
  type CivilDate,
} from './calendar-time.js';

/** 預先編譯的例外規則。 */
export interface CompiledException {
  readonly source: CalendarException;
  readonly fromDay: number;
  /** 區間長度（天），0 代表單日。 */
  readonly spanDays: number;
  readonly fromCivil: CivilDate;
  readonly untilDay: number | null;
  readonly kind: 'None' | 'Daily' | 'Weekly' | 'Monthly' | 'Yearly';
  /** Daily / Weekly 用的步進天數；其他 kind 為 0。 */
  readonly stepDays: number;
  /** Monthly / Yearly 用的步進月數；其他 kind 為 0。 */
  readonly stepMonths: number;
}

export function compileException(exception: CalendarException): CompiledException {
  const fromDay = parseDate(exception.from);
  const toDay = parseDate(exception.to);
  const recurrence = exception.recurrence;
  const kind = recurrence === undefined ? 'None' : recurrence.kind;
  const interval =
    recurrence === undefined || !Number.isFinite(recurrence.interval) || recurrence.interval < 1
      ? 1
      : Math.floor(recurrence.interval);
  return {
    source: exception,
    fromDay,
    spanDays: toDay - fromDay,
    fromCivil: civilFromDays(fromDay),
    untilDay: recurrence?.until === undefined ? null : parseDate(recurrence.until),
    kind,
    stepDays: kind === 'Daily' ? interval : kind === 'Weekly' ? interval * 7 : 0,
    stepMonths: kind === 'Monthly' ? interval : kind === 'Yearly' ? interval * 12 : 0,
  };
}

const occurrenceCovers = (rule: CompiledException, occurrenceStart: number, day: number): boolean => {
  if (rule.untilDay !== null && occurrenceStart > rule.untilDay) return false;
  const offset = day - occurrenceStart;
  return offset >= 0 && offset <= rule.spanDays;
};

/** 判斷某一天（距離 1970-01-01 的天數）是否落在此例外的任一次發生區間內。 */
export function exceptionCoversDay(rule: CompiledException, day: number, civil: CivilDate): boolean {
  if (day < rule.fromDay) return false;

  if (rule.kind === 'None') {
    return day - rule.fromDay <= rule.spanDays;
  }

  if (rule.stepDays > 0) {
    // 由最近一次發生往前回溯；只有當區間長度大於步進時才需要檢查更早的發生。
    const latest = Math.floor((day - rule.fromDay) / rule.stepDays);
    const lookBack = Math.floor(rule.spanDays / rule.stepDays) + 1;
    for (let k = latest; k >= 0 && k >= latest - lookBack; k -= 1) {
      if (occurrenceCovers(rule, rule.fromDay + k * rule.stepDays, day)) return true;
    }
    return false;
  }

  if (rule.stepMonths > 0) {
    const monthsDiff =
      (civil.year - rule.fromCivil.year) * 12 + (civil.month - rule.fromCivil.month);
    // 區間可能跨月／跨年，因此也檢查前一次可能的發生。
    const lookBack = Math.floor(rule.spanDays / 28) + 1;
    for (let s = monthsDiff; s >= 0 && s >= monthsDiff - lookBack * rule.stepMonths; s -= 1) {
      if (s % rule.stepMonths !== 0) continue;
      const totalMonths = (rule.fromCivil.year * 12 + (rule.fromCivil.month - 1)) + s;
      const year = Math.floor(totalMonths / 12);
      const month = totalMonths - year * 12 + 1;
      // 該次發生的起始日不存在（每月 31 號遇到 30 天的月份、每年 2/29 遇到平年）
      // 時，直接「跳過這一次發生」，不夾到月底也不捲到下個月 —— 捲月會讓假日
      // 悄悄落在錯誤的日期上，是最難察覺的一種錯。
      if (!isValidCivilDate(year, month, rule.fromCivil.day)) continue;
      const occurrenceStart = daysFromCivil(year, month, rule.fromCivil.day);
      if (occurrenceCovers(rule, occurrenceStart, day)) return true;
    }
    return false;
  }

  return day - rule.fromDay <= rule.spanDays;
}

/** 例外命中時該日的工作時段；`null` 代表「沿用該日原本的 weekDays 設定」。 */
export function exceptionWorkingTimes(
  exception: CalendarException,
): readonly WorkingTime[] | null {
  if (!exception.working) return [];
  if (exception.workingTimes.length === 0) return null;
  return exception.workingTimes;
}
